import React, { useContext, useMemo, useRef } from 'react';

import {
  Box,
  Text,
  Link,
  Flex,
  Button,
  DrawerBody,
  DrawerContent,
} from '@chakra-ui/react';

import { ProfileMenuLink } from './ProfileMenuLink';
import { IProfile } from '../../../Auth/types/auth';
import { useIsMobile } from '../../../hooks';
import { CollectionIcon, CopyIcon } from '../../../Icons';
import { ArrowRightIcon } from '../../../Icons/ArrowRightIcon';
import { AvatarIcon } from '../../../Icons/AvatarIcon';
import { CogIcon } from '../../../Icons/CogIcon';
import { HammerIcon } from '../../../Icons/HammerIcon';
import { HandTagIcon } from '../../../Icons/HandTagIcon';
import { HeartIcon } from '../../../Icons/HeartIcon';
import { LogOutIcon } from '../../../Icons/LogOutIcon';
import { LotOfCashIcon } from '../../../Icons/LotOfCashIcon';
import { OptionsIcon } from '../../../Icons/OptionsIcon';
import { QuestionIcon } from '../../../Icons/QuestionIcon';
import { SafeIcon } from '../../../Icons/SafeIcon';
import { ShoppingBagCheckIcon } from '../../../Icons/ShoppingBagCheckIcon';
import { SimpleDollarIcon } from '../../../Icons/SimpleDollarIcon';
import {
  EModalContent,
  useModalContext,
  useModalStore,
} from '../../../ModalProvider';
import { EColor } from '../../../Theme';
import { formatCurrency } from '../../../utilities/format/formatCurrency';
import { useLogout } from '../../../utilities/logout';
import { tracker } from '../../../utilities/tracker';
import { NavigationContext } from '../../context/NavigationContext';
import { useMonolithUrls } from '../../hooks/useMonolithUrls';
import { usePlaidRegistrationStatus } from '../../hooks/usePlaidRegistrationStatus';
import { getLink, getMonolithUrls } from '../../utils/monolithUrls';

interface IBOProfileMenuProps {
  pendingInvoiceCount: number;
  overdueInvoicesCount: number;
  unreadOffersCount: number;
  manageAccountUrl?: string;
  profile?: IProfile;
  onClose: () => void;
  avatarInitials?: string;
  sumInCents: number;
  enableCollections?: boolean;
}

export const BOProfileMenu = ({
  onClose,
  manageAccountUrl,
  profile,
  avatarInitials,
  pendingInvoiceCount,
  overdueInvoicesCount,
  unreadOffersCount,
  sumInCents,
  enableCollections,
}: IBOProfileMenuProps) => {
  const { helpCenterUrl } = getMonolithUrls();
  const { fanaticsCollectBaseUrl } = useModalContext();
  const { isNewPayoutFlowEnabled } = useContext(NavigationContext);
  const { logout } = useLogout();
  const modalStore = useModalStore();
  const urls = useMonolithUrls();
  const plaidRegistrationStatus = usePlaidRegistrationStatus();
  const vaultAddressRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile('sm');
  const initials =
    avatarInitials ||
    `${profile?.firstName?.[0]}${profile?.lastName?.[0]}`
      .trim()
      .toUpperCase() ||
    'FC';

  const isPremierAuctionApproved = plaidRegistrationStatus.label === 'Approved';
  const name = [profile?.firstName, profile?.lastName]
    .filter(Boolean)
    .join(' ');

  const totalWaitingInvoiceCount = pendingInvoiceCount + overdueInvoicesCount;

  const { dollars, cents } = useMemo(() => {
    if (sumInCents === 0) {
      return { dollars: formatCurrency(0), cents: '00' };
    }
    const dollarsValue = Math.floor(sumInCents / 100);
    const centsValue = sumInCents % 100;
    return {
      dollars: formatCurrency(dollarsValue, false),
      cents: centsValue.toString().padStart(2, '0'),
    };
  }, [sumInCents]);

  const track = (sectionClicked: string) => {
    tracker.track('Profile Menu Interaction', {
      sectionClicked,
    });
  };

  const copyVaultAddress = () => {
    if (vaultAddressRef.current) {
      const addressData = vaultAddressRef.current.innerText;
      navigator.clipboard.writeText(addressData).catch((error) => {
        console.error('Failed to copy to clipboard', error);
      });
    }
    track('Copy Vault Address');
  };

  const trackAndClose = (sectionClicked: string) => {
    track(sectionClicked);
    onClose();
  };

  const showWallet = () => {
    trackAndClose('Wallet');
    modalStore.navigate({ name: EModalContent.MY_WALLET });
  };

  const handleLogout = () => {
    trackAndClose('Log Out');
    logout(fanaticsCollectBaseUrl);
  };

  return (
    <DrawerContent
      aria-label="member navigation"
      background="transparent"
      border={`1px solid ${EColor.Neutral150}`}
      borderRadius="6px"
      boxShadow="0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)"
      height="100%"
      id="member-navigation"
      marginInline="unset !important"
      marginX={{ base: '15px', md: '30px' }}
      marginY="20px"
      maxH="calc(100vh - 90px)"
      maxWidth="400px"
      overflow="hidden"
      right={{ base: undefined, md: '6% !important' }}
      role="navigation"
      top="50px !important"
      w="100%"
      zIndex={2001}
    >
      <DrawerBody
        background={EColor.Neutral50}
        color={EColor.Black}
        display="flex"
        flex="unset"
        flexDirection="column"
        height="100%"
        margin="0"
        maxHeight="calc(100vh - 90px)"
        padding={0}
      >
        {/* Header */}
        <Box
          backgroundColor={EColor.Neutral50}
          flexShrink={0}
          padding="16px"
          paddingBottom="8px"
          position="sticky"
          top="0"
          zIndex="10"
        >
          <Flex
            alignItems="center"
            flexWrap="nowrap"
            gap="8px"
            justifyContent="flex-start"
            overflow="hidden"
            width="100%"
          >
            <Flex
              alignItems="center"
              backgroundColor={EColor.Blue75}
              borderRadius="full"
              color={EColor.White}
              fontSize="14px"
              fontWeight="700"
              height="40px"
              justifyContent="center"
              minWidth="40px"
              width="40px"
            >
              {initials}
            </Flex>
            <Flex
              alignItems={isPremierAuctionApproved ? 'center' : 'flex-start'}
              flexDirection={isPremierAuctionApproved ? 'row' : 'column'}
              justifyContent={
                isPremierAuctionApproved ? 'space-between' : 'center'
              }
              width="100%"
            >
              <Text fontSize="16px" fontWeight="bold" noOfLines={1}>
                {name}
              </Text>
              {isPremierAuctionApproved ? (
                <Link
                  _hover={{ textDecoration: 'none' }}
                  href={getLink(fanaticsCollectBaseUrl, 'premierSRP')}
                  onClick={() =>
                    trackAndClose(
                      'Redirect to Premier Auction SRP from profile drawer'
                    )
                  }
                >
                  <Box
                    alignItems="center"
                    background="linear-gradient(100deg, #CFB976 0%, #F5E0A2 21%, #F9ECC3 55%, #D5B34C 100%)"
                    borderRadius="4px"
                    display="flex"
                    fontSize="12px"
                    fontWeight="bold"
                    justifyContent="center"
                    lineHeight="14px"
                    paddingX="8px"
                    paddingY="4px"
                  >
                    Premier Auction
                  </Box>
                </Link>
              ) : (
                <Link
                  color={EColor.Neutral700}
                  fontSize="14px"
                  href={getLink(fanaticsCollectBaseUrl, 'premierSRP')}
                  textDecoration="underline"
                  onClick={() => trackAndClose('Get Premier Auction Approved')}
                >
                  Get Premier Auction approved
                </Link>
              )}
            </Flex>
          </Flex>
        </Box>

        {/* Content */}
        <Box
          display="flex"
          flex="1"
          flexDirection="column"
          gap="16px"
          minHeight="0"
          overflowY="auto"
          padding="16px"
          paddingTop="8px"
        >
          {/* Wallet Section */}
          <Box
            alignItems="flex-start"
            backgroundColor={EColor.Neutral55}
            backgroundImage="url(https://assets.fanaticscollect.com/backgrounds/ProfileMenuWalletBadgeBackground.png)"
            backgroundPosition="center"
            backgroundRepeat="no-repeat"
            backgroundSize="cover"
            borderRadius="8px"
            cursor="pointer"
            display="flex"
            flexDirection="column"
            flexShrink={0}
            fontSize="14px"
            justifyContent="space-between"
            minHeight="120px"
            overflow="hidden"
            padding="16px"
            width="100%"
            onClick={showWallet}
          >
            <Flex
              justifyContent="space-between"
              textColor={EColor.White}
              width="100%"
            >
              <Text fontSize="14px" fontWeight="bold" textTransform="none">
                Wallet
              </Text>
              <Box>
                <ArrowRightIcon color={EColor.White} h="16px" w="16px" />
              </Box>
            </Flex>
            <Text
              color={EColor.White}
              fontSize="12px"
              opacity="0.75"
              textTransform="none"
            >
              Manage your payment methods and balances
            </Text>
            <Text color={EColor.White} fontSize="12px" textTransform="none">
              <Text
                as="span"
                color={EColor.White}
                fontSize="30px"
                fontWeight="700"
                lineHeight="40px"
              >
                {`${dollars}`}
                <Text
                  as="span"
                  color={EColor.White}
                  fontSize="20px"
                  lineHeight="28px"
                >
                  .{cents}
                </Text>
              </Text>
            </Text>
            <Text
              color={EColor.White}
              fontSize="12px"
              opacity="0.75"
              textTransform="none"
            >
              Available balance
            </Text>
          </Box>

          {/* Collection Section */}
          {isMobile && (
            <Box
              alignItems="flex-start"
              backgroundColor={EColor.White}
              borderRadius="8px"
              display="flex"
              flexDirection="column"
              flexShrink={0}
              fontSize="14px"
              justifyContent="space-between"
              overflow="hidden"
              width="100%"
            >
              <ProfileMenuLink
                body="View and manage everything you own"
                href={getLink(
                  fanaticsCollectBaseUrl,
                  enableCollections ? 'collection' : 'vault'
                )}
                icon={<CollectionIcon height="20px" width="20px" />}
                title="Collection"
                onClick={() => trackAndClose('Collection')}
              />
            </Box>
          )}

          {/* Main Menu Section */}
          <Box
            alignItems="flex-start"
            backgroundColor={EColor.White}
            borderRadius="8px"
            display="flex"
            flexDirection="column"
            flexShrink={0}
            fontSize="14px"
            justifyContent="space-between"
            overflow="hidden"
            width="100%"
          >
            <ProfileMenuLink
              body="Track and view your purchases"
              href={getLink(fanaticsCollectBaseUrl, 'activityOrders')}
              icon={<ShoppingBagCheckIcon h="20px" w="20px" />}
              notification={totalWaitingInvoiceCount}
              title="Orders"
              urgentNotification={overdueInvoicesCount > 0}
              onClick={() => trackAndClose('Orders')}
            />
            <ProfileMenuLink
              body="View your active and past auctions bids"
              href={getLink(fanaticsCollectBaseUrl, 'activityBidsActiveWeekly')}
              icon={<HammerIcon h="20px" w="20px" />}
              title="Bids"
              onClick={() => trackAndClose('Bids')}
            />
            <ProfileMenuLink
              body="Keep tabs on saved items"
              href={getLink(fanaticsCollectBaseUrl, 'activityWatchlistBuyNow')}
              icon={<HeartIcon h="20px" w="20px" />}
              title="Watchlist"
              onClick={() => trackAndClose('Watchlist')}
            />
            <ProfileMenuLink
              body="Track offers you've made or received"
              href={getLink(fanaticsCollectBaseUrl, 'activityOffers')}
              icon={<LotOfCashIcon h="20px" w="20px" />}
              title="Offers"
              onClick={() => trackAndClose('Offers')}
            />
            <ProfileMenuLink
              body="Monitor your active listings and sales"
              href={getLink(fanaticsCollectBaseUrl, 'activitySelling')}
              icon={<HandTagIcon h="20px" w="20px" />}
              title="Selling"
              onClick={() => trackAndClose('Selling')}
            />
            {!isNewPayoutFlowEnabled && (
              <ProfileMenuLink
                body="An overall view of your account"
                href={urls.memberSummaryUrl}
                icon={<OptionsIcon h="20px" w="20px" />}
                title="Summary"
                onClick={() => trackAndClose('Summary')}
              />
            )}
            <ProfileMenuLink
              body="View balance, summaries, and transactions"
              href={getLink(fanaticsCollectBaseUrl, 'activityAccounting')}
              icon={<SimpleDollarIcon h="20px" w="20px" />}
              title="Accounting"
              onClick={() => trackAndClose('Accounting')}
            />
            <ProfileMenuLink
              body="Review submitted cards and progress"
              href={getLink(fanaticsCollectBaseUrl, 'submissions')}
              icon={<SafeIcon h="20px" mb="1px" w="20px" />}
              title="Submissions"
              onClick={() => trackAndClose('Submissions')}
            />
          </Box>

          {/* Vault Section */}
          <Box
            alignItems="flex-start"
            backgroundColor={EColor.White}
            borderRadius="8px"
            display="flex"
            flexDirection="column"
            flexShrink={0}
            fontSize="14px"
            gap="2px"
            justifyContent="space-between"
            overflow="hidden"
            paddingX="16px"
            paddingY="12px"
            width="100%"
          >
            <Flex justifyContent="space-between" width="100%">
              <Text fontSize="14px" fontWeight="bold">
                My Vault address
              </Text>
              <Button
                backgroundColor="transparent"
                border="none"
                display="flex"
                flexWrap="nowrap"
                gap="4px"
                height="auto"
                minWidth="auto"
                padding="0"
                type="button"
                onClick={copyVaultAddress}
              >
                <CopyIcon h="16px" mt="1px" w="16px" />
              </Button>
            </Flex>
            <Box color={EColor.Neutral700} ref={vaultAddressRef}>
              <Text fontSize="12px">
                {`${profile?.firstName} ${profile?.lastName}`}
              </Text>
              <Text fontSize="12px">
                {profile?.vaultAddress?.street}
                {profile?.vaultAccountId
                  ? `, ID ${profile?.vaultAccountId}`
                  : ''}
              </Text>
              <Text fontSize="12px">{profile?.vaultAddress?.cityStateZip}</Text>
            </Box>
            <Button
              _hover={{ textDecoration: 'none' }}
              as={Link}
              backgroundColor={EColor.BrandDark}
              color={EColor.White}
              fontSize="12px"
              href={getLink(fanaticsCollectBaseUrl, 'submissionCreate')}
              marginTop="6px"
              padding="8px 16px"
              size="sm"
              textTransform="none"
            >
              Submit items to Vault
            </Button>
          </Box>

          {/* Preferences Section */}
          <Box
            alignItems="flex-start"
            backgroundColor={EColor.White}
            borderRadius="8px"
            display="flex"
            flexDirection="column"
            flexShrink={0}
            fontSize="14px"
            justifyContent="space-between"
            overflow="hidden"
            width="100%"
          >
            <ProfileMenuLink
              body="Edit your Collect settings"
              href={getLink(fanaticsCollectBaseUrl, 'preferences')}
              icon={<CogIcon h="20px" w="20px" />}
              title="Preferences"
              onClick={() => trackAndClose('Preferences')}
            />
          </Box>

          {/* External Links Section */}
          <Box
            alignItems="flex-start"
            backgroundColor={EColor.White}
            borderRadius="8px"
            display="flex"
            flexDirection="column"
            flexShrink={0}
            fontSize="14px"
            justifyContent="space-between"
            overflow="hidden"
            width="100%"
          >
            <ProfileMenuLink
              external
              body="Update personal and login details"
              href={manageAccountUrl}
              icon={<AvatarIcon h="20px" w="20px" />}
              title="Account"
              onClick={() => trackAndClose('Manage Account')}
            />
            <ProfileMenuLink
              external
              body="Get help, answers, and updates"
              href={helpCenterUrl}
              icon={<QuestionIcon h="20px" w="20px" />}
              title="Help Center"
              onClick={() => trackAndClose('Help Center')}
            />
          </Box>

          {/* Logout Section */}
          <Box
            alignItems="flex-start"
            backgroundColor={EColor.White}
            borderRadius="8px"
            display="flex"
            flexDirection="column"
            flexShrink={0}
            fontSize="14px"
            justifyContent="space-between"
            overflow="hidden"
            width="100%"
          >
            <ProfileMenuLink
              body="Securely log out of Collect"
              icon={<LogOutIcon h="20px" w="20px" />}
              title="Log out"
              onClick={handleLogout}
            />
          </Box>
        </Box>
      </DrawerBody>
    </DrawerContent>
  );
};
